import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { usePHRItems, useTHRItems } from '@/hooks';
import { useCreateLossRecord } from '@/hooks/mutations/asset-loss-management';
import { EXCLUDE_PNP_TYPE_KEEP_NULL } from '@/utils/iemFilters';
import { useNotifications } from '@/stores/useNotificationStore';
import { entityService } from '@/services/EntityService';
import { documentService } from '@/services/documents';
import type { DocumentPreviewResponse } from '@/services/documents';
import { lossRecordService } from '@/services/lossRecordService';
import { openBase64PdfInWindow, openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { DocumentFieldsModal } from '@/components/documents/DocumentFieldsModal';
import { toYYYYMMDD } from '@/utils/dateFormat';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTableCell } from '@/components/shared/SortableTableCell';
import { logger } from '@equip/utils/logger';
import { usePersonLabels } from '@/hooks/usePersonLabels';

const log = logger.child({ component: 'DA7923Modal' });

const CAUSE_OF_CHARGE_OPTIONS = ['Lost', 'Damaged', 'Destroyed'] as const;
type CauseOfCharge = (typeof CAUSE_OF_CHARGE_OPTIONS)[number] | '';

const DEPRECIATION_OPTIONS = [0, 5, 10, 15, 20, 25] as const;

interface PersonOption {
  id: string;
  display_name: string;
  entity_identifier: string | null | undefined;
  rank: string | null;
  unit: string | null;
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
}

interface HandReceiptItem {
  id: string;
  asset_id: string | null;
  item: string;
  lin: string | null;
  nsn: string | null;
  unitPrice: number | null;
  receiptType: 'PHR' | 'THR';
  issueDate: string | null;
}

export interface DA7923ModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Format person name as "Last, First, MI" for the DA7923 form.
 * Falls back to display_name if structured name fields are unavailable.
 */
function formatPersonName(person: PersonOption): string {
  const last = person.lastName?.trim();
  const first = person.firstName?.trim();
  if (!last && !first) return person.display_name;

  const mi = person.middleName?.trim();
  const initial = mi ? mi.charAt(0).toUpperCase() : '';

  const parts = [last, first, initial].filter(Boolean);
  return parts.join(', ');
}

/**
 * Compute totalPrice per item and totalAmount from the edited payload.
 * Applies depreciation to unit prices, appends original price to description,
 * combines LIN/NSN in the NIIN column, and adds a depreciation statement row.
 */
function computeTotals(
  payload: Record<string, unknown>,
  depreciationPct: number,
): Record<string, unknown> {
  const items = payload.items as Array<Record<string, unknown>> | undefined;
  if (!items) return payload;

  const depRate = depreciationPct > 0 ? 1 - depreciationPct / 100 : 1;

  let grandTotal = 0;
  const coercedItems = items.map((item) => {
    const qty = Number(item.quantity) || 1;
    const originalPrice = parseFloat(String(item.unitPrice || '0')) || 0;
    const depreciatedPrice = depreciationPct > 0 ? originalPrice * depRate : originalPrice;
    const total = qty * depreciatedPrice;
    grandTotal += total;

    // Combine LIN and NSN for the NIIN / Material Number column
    const lin = String(item.lin || '');
    const nsn = String(item.nsn || '');
    const niinDisplay = lin && nsn ? `${lin}/${nsn}` : lin || nsn;

    // Append original unit price to the item description
    const nomenclature = String(item.nomenclature || '');
    const descriptionWithPrice =
      originalPrice > 0 ? `${nomenclature} ($${originalPrice.toFixed(2)})` : nomenclature;

    return {
      ...item,
      quantity: qty,
      nsn: niinDisplay,
      nomenclature: descriptionWithPrice,
      unitPrice: depreciatedPrice > 0 ? depreciatedPrice : undefined,
      totalPrice: total > 0 ? total : undefined,
    };
  });

  // Append depreciation statement as a virtual row after the last real item
  if (depreciationPct > 0 && coercedItems.length < 12) {
    coercedItems.push({
      nsn: '',
      nomenclature: `${depreciationPct}% Depreciation allowed per AR 735-5, Appendix B`,
      quantity: undefined as unknown as number,
      unitPrice: undefined,
      totalPrice: undefined,
    });
  }

  return {
    ...payload,
    items: coercedItems,
    totalAmount: grandTotal > 0 ? grandTotal : undefined,
  };
}

export const DA7923Modal: React.FC<DA7923ModalProps> = ({ open, onClose, onSuccess }) => {
  const notifications = useNotifications();
  const person = usePersonLabels('front_window');
  const createMutation = useCreateLossRecord();

  // Person search state
  const [personSearchInput, setPersonSearchInput] = useState('');
  const [personOptions, setPersonOptions] = useState<PersonOption[]>([]);
  const [personLoading, setPersonLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<PersonOption | null>(null);

  // HR items state
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());

  // Form state
  const [causeOfCharge, setCauseOfCharge] = useState<CauseOfCharge>('');
  const [depreciationPct, setDepreciationPct] = useState<number>(0);
  const [comments, setComments] = useState('');

  // Document fields modal state
  const [fieldsModalOpen, setFieldsModalOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [previewData, setPreviewData] = useState<DocumentPreviewResponse | null>(null);
  const pendingResultRef = useRef<{ id: string } | null>(null);
  const docWindowRef = useRef<Window | null>(null);
  const assetCountRef = useRef(0);
  const depreciationPctRef = useRef(0);

  // Fetch PHR/THR items for selected person (PnP items excluded)
  const { data: phrData = [], isLoading: phrLoading } = usePHRItems(
    selectedPerson?.id ?? '',
    EXCLUDE_PNP_TYPE_KEEP_NULL,
  );
  const { data: thrData = [], isLoading: thrLoading } = useTHRItems(
    selectedPerson?.id ?? '',
    EXCLUDE_PNP_TYPE_KEEP_NULL,
  );

  const hrItemsLoading = phrLoading || thrLoading;

  // Combine PHR and THR items
  const handReceiptItems = useMemo<HandReceiptItem[]>(() => {
    const phrItems: HandReceiptItem[] = phrData
      .filter((item) => item.status?.toLowerCase() !== 'closed')
      .map((item) => ({
        id: item.id,
        asset_id: item.asset_id ?? null,
        item: item.item,
        lin: item.lin ?? null,
        nsn: item.nsn ?? null,
        unitPrice: item.unitPrice ?? null,
        receiptType: 'PHR' as const,
        issueDate: item.issueDate ?? null,
      }));

    const thrItems: HandReceiptItem[] = thrData
      .filter((item) => item.status?.toLowerCase() !== 'closed')
      .map((item) => ({
        id: item.id,
        asset_id: item.asset_id ?? null,
        item: item.item,
        lin: item.lin ?? null,
        nsn: item.nsn ?? null,
        unitPrice: item.unitPrice ?? null,
        receiptType: 'THR' as const,
        issueDate: item.issueDate ?? null,
      }));

    return [...thrItems, ...phrItems];
  }, [phrData, thrData]);

  // Group hand receipt items by LIN+NSN for consolidated display with quantity
  const groupedHandReceiptItems = useMemo(() => {
    const grouped = new Map<
      string,
      { item: HandReceiptItem; quantity: number; assetIds: string[] }
    >();

    handReceiptItems.forEach((hrItem) => {
      if (!hrItem.asset_id) return;
      const lin = (hrItem.lin ?? '').trim().toUpperCase();
      const nsn = (hrItem.nsn ?? '').trim().toUpperCase();
      const key = nsn ? `${lin}|${nsn}` : `asset:${hrItem.id}`;
      const existing = grouped.get(key);

      if (!existing) {
        grouped.set(key, { item: hrItem, quantity: 1, assetIds: [hrItem.asset_id] });
        return;
      }

      existing.quantity += 1;
      existing.assetIds.push(hrItem.asset_id);
    });

    return Array.from(grouped.values());
  }, [handReceiptItems]);

  // Flat rows for sorting — each grouped row has all base fields plus quantity/assetIds
  const groupedDisplayRows = useMemo(
    () =>
      groupedHandReceiptItems.map((g) => ({
        ...g.item,
        quantity: g.quantity,
        assetIds: g.assetIds,
      })),
    [groupedHandReceiptItems],
  );

  // Get selected hand receipt items, deduplicated by asset_id.
  // The same asset can appear on both PHR and THR; without dedup the
  // duplicate entry inflates quantities and causes items to appear on
  // both page 1 and the continuation sheet of the generated form.
  const selectedHandReceiptItems = useMemo(() => {
    const byAssetId = new Map<string, HandReceiptItem>();
    for (const item of handReceiptItems) {
      if (!item.asset_id || !selectedAssetIds.has(item.asset_id)) continue;
      const existing = byAssetId.get(item.asset_id);
      if (!existing || (item.nsn && !existing.nsn)) {
        byAssetId.set(item.asset_id, item);
      }
    }
    return Array.from(byAssetId.values());
  }, [handReceiptItems, selectedAssetIds]);

  // Aggregate selected items by LIN+NSN for the document payload
  const aggregatedSelectedItems = useMemo(() => {
    const grouped = new Map<string, { item: HandReceiptItem; quantity: number }>();

    selectedHandReceiptItems.forEach((hrItem) => {
      const lin = (hrItem.lin ?? '').trim().toUpperCase();
      const nsn = (hrItem.nsn ?? '').trim().toUpperCase();
      const key = nsn ? `${lin}|${nsn}` : `asset:${hrItem.id}`;
      const existing = grouped.get(key);

      if (!existing) {
        grouped.set(key, { item: hrItem, quantity: 1 });
        return;
      }

      existing.quantity += 1;
    });

    return Array.from(grouped.values());
  }, [selectedHandReceiptItems]);

  const {
    sortField: hrSortField,
    sortDirection: hrSortDirection,
    onSort: onHrSort,
    sortedRows: sortedHrItems,
  } = useTableSort(groupedDisplayRows as unknown as Record<string, unknown>[]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedPerson(null);
      setPersonSearchInput('');
      setPersonOptions([]);
      setSelectedAssetIds(new Set());
      setCauseOfCharge('');
      setDepreciationPct(0);
      setComments('');
      setFieldsModalOpen(false);
      setPreviewData(null);
      setIsLoadingPreview(false);
      setIsGeneratingPdf(false);
      pendingResultRef.current = null;
      docWindowRef.current = null;
    }
  }, [open]);

  // Search for persons
  useEffect(() => {
    if (!personSearchInput.trim() || personSearchInput.length < 2) {
      setPersonOptions([]);
      return;
    }

    const controller = new AbortController();
    setPersonLoading(true);

    const searchPersons = async () => {
      try {
        const results = await entityService.searchEntities(personSearchInput, 'person');
        if (!controller.signal.aborted) {
          setPersonOptions(
            results.map((e) => ({
              id: e.id,
              display_name: e.display_name ?? 'Unknown',
              entity_identifier: e.entity_identifier,
              rank: (e.entity_data?.rank as string) ?? null,
              unit: (e.entity_data?.unit as string) ?? null,
              firstName: (e.entity_data?.first_name as string) ?? null,
              lastName: (e.entity_data?.last_name as string) ?? null,
              middleName: (e.entity_data?.middle_name as string) ?? null,
            })),
          );
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          log.error('Person search failed', { error: String(error) });
        }
      } finally {
        if (!controller.signal.aborted) {
          setPersonLoading(false);
        }
      }
    };

    const debounce = setTimeout(searchPersons, 300);
    return () => {
      clearTimeout(debounce);
      controller.abort();
    };
  }, [personSearchInput]);

  // Handle asset selection
  const handleToggleGroup = useCallback((assetIds: string[]) => {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      const allSelected = assetIds.every((id) => next.has(id));
      if (allSelected) {
        assetIds.forEach((id) => next.delete(id));
      } else {
        assetIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const handleSetGroupCount = useCallback((assetIds: string[], count: number) => {
    const clamped = Math.max(0, Math.min(count, assetIds.length));
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      assetIds.forEach((id) => next.delete(id));
      assetIds.slice(0, clamped).forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allAssetIds = handReceiptItems
      .filter((item) => item.asset_id)
      .map((item) => item.asset_id!);
    setSelectedAssetIds(new Set(allAssetIds));
  }, [handReceiptItems]);

  const handleDeselectAll = useCallback(() => {
    setSelectedAssetIds(new Set());
  }, []);

  // Step 1: Create loss record, then fetch preview config and show document fields modal
  const handleCreate = useCallback(async () => {
    if (!selectedPerson) {
      notifications.warning(
        `Select ${person.label}`,
        `Please select ${person.article} ${person.lower} first.`,
      );
      return;
    }

    if (selectedAssetIds.size === 0) {
      notifications.warning('Select Assets', 'Please select at least one asset.');
      return;
    }

    if (!causeOfCharge) {
      notifications.warning('Select Cause of Charge', 'Please select a cause of charge.');
      return;
    }

    // Capture values before any async work for use in callbacks
    assetCountRef.current = selectedAssetIds.size;
    depreciationPctRef.current = depreciationPct;

    let result: { id: string };
    try {
      result = await createMutation.mutateAsync({
        transactionType: 'DA7923',
        personId: selectedPerson.id,
        assetIds: Array.from(selectedAssetIds),
        comments: causeOfCharge + (comments.trim() ? ` - ${comments.trim()}` : ''),
      });
    } catch (error) {
      log.error('Failed to create DA7923', { error: String(error) });
      notifications.error(
        'Failed to Create',
        error instanceof Error ? error.message : 'An unexpected error occurred.',
      );
      return;
    }

    // Store the result for document generation
    pendingResultRef.current = result;

    // Build the payload for the preview endpoint (same structure the backend expects)
    // statementDate is formatted YYYYMMDD; statementNumber is reserved by the backend
    const payload: Record<string, unknown> = {
      statementDate: toYYYYMMDD(),
      fromUnit: selectedPerson.unit ?? '',
      disStationNumber: '',
      accountingClassification: '',
      station: '',
      individual: {
        name: formatPersonName(selectedPerson),
        rank: selectedPerson.rank ?? '',
        individualId: (selectedPerson.entity_identifier ?? '').replace(
          /^INDV-/,
          person.individualIdPrefix,
        ),
      },
      causeOfCharge,
      depreciationPct,
      items: aggregatedSelectedItems.map(({ item, quantity }) => ({
        nomenclature: item.item,
        lin: item.lin ?? '',
        nsn: item.nsn ?? '',
        quantity,
        unitPrice: item.unitPrice ?? '',
      })),
      paymentMethod: { cash: false, payrollDeduction: false },
    };

    // Fetch editable fields from the backend (driven by mapping config in DB)
    setIsLoadingPreview(true);
    try {
      const preview = await documentService.getDocumentPreview({
        documentType: 'DA7923',
        payload,
      });
      setIsLoadingPreview(false);
      setPreviewData(preview);
      setFieldsModalOpen(true);
    } catch (previewError) {
      setIsLoadingPreview(false);
      log.warn('DA7923 preview unavailable, record was created successfully', {
        error: String(previewError),
      });
      notifications.success(
        'Statement of Charges Created',
        `Statement of charges created for ${assetCountRef.current} asset(s). Document fields could not be loaded — you can regenerate the form later.`,
      );
      onSuccess?.();
      onClose();
    }
  }, [
    selectedPerson,
    selectedAssetIds,
    aggregatedSelectedItems,
    causeOfCharge,
    depreciationPct,
    comments,
    createMutation,
    notifications,
    onSuccess,
    onClose,
  ]);

  // Step 2: Generate document with user-edited payload
  const handleFieldsConfirm = useCallback(
    async (editedPayload: Record<string, unknown>) => {
      const result = pendingResultRef.current;

      if (!result) return;

      // Pre-open window now (user just clicked "Generate Document" — user-gesture context)
      const docWindow = preOpenWindow();
      docWindowRef.current = docWindow;

      setIsGeneratingPdf(true);

      let docFailed = false;

      try {
        // Compute totalPrice per item and grand total (with depreciation applied)
        const finalPayload = computeTotals(editedPayload, depreciationPctRef.current);

        const docResult = await documentService.generateDocument({
          documentType: 'DA7923',
          payload: {
            ...finalPayload,
            relatedEntityId: result.id,
          },
          options: { metadata: { relatedEntityId: result.id } },
          nonExpendableDocumentNumber: (finalPayload.statementNumber as string) || undefined,
        });

        // Record the generated document ID on the loss record (best-effort)
        try {
          await lossRecordService.addGeneratedDocumentId(result.id, docResult.documentId);
        } catch (linkErr) {
          log.warn('Failed to record DA7923 document ID on loss record', {
            id: result.id,
            documentId: docResult.documentId,
            error: String(linkErr),
          });
        }

        if (docResult.outputBuffer) {
          const opened = openBase64PdfInWindow(docResult.outputBuffer, docWindow);
          if (!opened && docResult.downloadUrl) {
            // Inline open failed — fall back to authenticated download
            const fallbackUrl = documentService.resolveDownloadUrl(docResult.downloadUrl);
            if (fallbackUrl) await openDocumentWithAuth(fallbackUrl);
          }
        } else if (docResult.downloadUrl) {
          const url = documentService.resolveDownloadUrl(docResult.downloadUrl);
          if (url) {
            const opened = await openDocumentWithAuth(url, docWindow);
            if (!opened) {
              // openDocumentWithAuth already closed the pre-opened window on failure
              log.error('Failed to open DA7923 document', { downloadUrl: docResult.downloadUrl });
              docFailed = true;
            }
          } else {
            if (docWindow && !docWindow.closed) docWindow.close();
            log.error('resolveDownloadUrl returned null', { downloadUrl: docResult.downloadUrl });
            docFailed = true;
          }
        } else {
          if (docWindow && !docWindow.closed) docWindow.close();
          log.error('No downloadUrl in DA7923 generate response');
          docFailed = true;
        }
      } catch (docError) {
        if (docWindow && !docWindow.closed) docWindow.close();
        log.error('Failed to generate DA 7923', { error: String(docError) });
        docFailed = true;
      }

      setIsGeneratingPdf(false);
      setFieldsModalOpen(false);

      if (docFailed) {
        notifications.warning(
          'Document Generation Issue',
          `Statement of charges created for ${assetCountRef.current} asset(s), but the DA7923 form could not be opened. You can regenerate it later.`,
        );
      } else {
        notifications.success(
          'Statement of Charges Created',
          `Statement of charges created for ${assetCountRef.current} asset(s).`,
        );
      }

      onSuccess?.();
      onClose();
    },
    [notifications, onSuccess, onClose],
  );

  // Skip document generation but still complete the creation
  const handleFieldsSkip = useCallback(() => {
    const docWindow = docWindowRef.current;
    if (docWindow && !docWindow.closed) docWindow.close();

    setFieldsModalOpen(false);

    notifications.success(
      'Statement of Charges Created',
      `Statement of charges created for ${assetCountRef.current} asset(s). Document generation skipped.`,
    );

    onSuccess?.();
    onClose();
  }, [notifications, onSuccess, onClose]);

  const handleFieldsClose = useCallback(() => {
    // If closing without confirming, close the pre-opened window
    const docWindow = docWindowRef.current;
    if (docWindow && !docWindow.closed) docWindow.close();
    setFieldsModalOpen(false);

    // Record was already created, so still notify success
    notifications.success(
      'Statement of Charges Created',
      `Statement of charges created for ${assetCountRef.current} asset(s). Document generation cancelled.`,
    );

    onSuccess?.();
    onClose();
  }, [notifications, onSuccess, onClose]);

  const canCreate =
    selectedPerson &&
    selectedAssetIds.size > 0 &&
    causeOfCharge !== '' &&
    !createMutation.isPending &&
    !isLoadingPreview;

  return (
    <>
      <Dialog open={open && !fieldsModalOpen} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="span">
            Create Statement of Charges
          </Typography>
          <Typography variant="body2" color="text.secondary">
            DA7923 - Statement of Charges for Government Property
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Step 1: Select Service Member */}
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                1. Select {person.label}
              </Typography>
              <Autocomplete
                options={personOptions}
                getOptionLabel={(option) =>
                  `${option.display_name}${option.entity_identifier ? ` (${option.entity_identifier.replace(/^INDV-/, person.individualIdPrefix)})` : ''}`
                }
                value={selectedPerson}
                onChange={(_, newValue) => {
                  setSelectedPerson(newValue);
                  setSelectedAssetIds(new Set());
                }}
                inputValue={personSearchInput}
                onInputChange={(_, newInputValue) => setPersonSearchInput(newInputValue)}
                loading={personLoading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Search by name or ${person.individualIdLabel}`}
                    placeholder="Start typing to search..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {personLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  personSearchInput.length < 2
                    ? 'Type at least 2 characters to search'
                    : `No ${person.lowerPlural} found`
                }
              />
            </Box>

            {/* Step 2: Select Hand Receipt Items */}
            {selectedPerson && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="medium">
                    2. Select Hand Receipt Items ({selectedAssetIds.size} selected)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={handleSelectAll} disabled={hrItemsLoading}>
                      Select All
                    </Button>
                    <Button size="small" onClick={handleDeselectAll} disabled={hrItemsLoading}>
                      Deselect All
                    </Button>
                  </Box>
                </Box>

                {hrItemsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : handReceiptItems.length === 0 ? (
                  <Alert severity="info">
                    No items found on this {person.lower}'s hand receipt.
                  </Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <SortableTableCell
                            field="item"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            Item
                          </SortableTableCell>
                          <SortableTableCell
                            field="quantity"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            Qty
                          </SortableTableCell>
                          <SortableTableCell
                            field="lin"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            LIN
                          </SortableTableCell>
                          <SortableTableCell
                            field="nsn"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            NSN
                          </SortableTableCell>
                          <SortableTableCell
                            field="receiptType"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            Type
                          </SortableTableCell>
                          <SortableTableCell
                            field="issueDate"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            Issue Date
                          </SortableTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(
                          sortedHrItems as unknown as (HandReceiptItem & {
                            quantity: number;
                            assetIds: string[];
                          })[]
                        ).map((row) => {
                          const selectedCount = row.assetIds.filter((id) =>
                            selectedAssetIds.has(id),
                          ).length;
                          const allSelected = selectedCount === row.assetIds.length;
                          const someSelected = selectedCount > 0 && !allSelected;
                          return (
                            <TableRow
                              key={row.id}
                              hover
                              onClick={() => handleToggleGroup(row.assetIds)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={allSelected}
                                  indeterminate={someSelected}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => handleToggleGroup(row.assetIds)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              </TableCell>
                              <TableCell>{row.item}</TableCell>
                              <TableCell
                                sx={{ textAlign: 'center' }}
                                onClick={
                                  row.quantity > 1
                                    ? (e: React.MouseEvent) => e.stopPropagation()
                                    : undefined
                                }
                              >
                                {row.quantity > 1 ? (
                                  <TextField
                                    type="number"
                                    size="small"
                                    variant="standard"
                                    value={selectedCount}
                                    inputProps={{
                                      min: 0,
                                      max: row.quantity,
                                      style: { textAlign: 'center', width: 28, padding: 0 },
                                    }}
                                    onChange={(e) =>
                                      handleSetGroupCount(
                                        row.assetIds,
                                        parseInt(e.target.value) || 0,
                                      )
                                    }
                                    helperText={`of ${row.quantity}`}
                                    FormHelperTextProps={{
                                      sx: { textAlign: 'center', mt: 0, fontSize: '0.7rem' },
                                    }}
                                    sx={{ width: 56 }}
                                  />
                                ) : (
                                  row.quantity
                                )}
                              </TableCell>
                              <TableCell>{row.lin ?? '—'}</TableCell>
                              <TableCell>{row.nsn ?? '—'}</TableCell>
                              <TableCell>{row.receiptType}</TableCell>
                              <TableCell>
                                {row.issueDate ? new Date(row.issueDate).toLocaleDateString() : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Step 3: Loss Details */}
            {selectedPerson && selectedAssetIds.size > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  3. Loss Details
                </Typography>
                <Stack spacing={2}>
                  <FormControl fullWidth required size="small">
                    <InputLabel id="cause-of-charge-label">Cause of Charge</InputLabel>
                    <Select
                      labelId="cause-of-charge-label"
                      value={causeOfCharge}
                      label="Cause of Charge"
                      onChange={(e) => setCauseOfCharge(e.target.value as CauseOfCharge)}
                    >
                      {CAUSE_OF_CHARGE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel id="depreciation-pct-label">Depreciation Percentage</InputLabel>
                    <Select
                      labelId="depreciation-pct-label"
                      value={depreciationPct}
                      label="Depreciation Percentage"
                      onChange={(e) => setDepreciationPct(Number(e.target.value))}
                    >
                      {DEPRECIATION_OPTIONS.map((pct) => (
                        <MenuItem key={pct} value={pct}>
                          {pct === 0 ? 'None (0%)' : `${pct}%`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Additional Comments (Optional)"
                    placeholder="Any additional notes..."
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                  />
                </Stack>
              </Box>
            )}

            {/* Info about what Create does */}
            {selectedPerson && selectedAssetIds.size > 0 && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Create</strong> will:
                </Typography>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                  <li>Create an Asset Loss Management record</li>
                  <li>Open the DA7923 form fields for review before generating</li>
                  <li>
                    Assets will <strong>remain on the Hand Receipt</strong> until the record is
                    completed
                  </li>
                </ul>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={createMutation.isPending || isLoadingPreview}
          >
            Cancel
          </Button>
          <ViewerGuardedButton
            variant="contained"
            onClick={handleCreate}
            disabled={!canCreate}
            startIcon={
              createMutation.isPending || isLoadingPreview ? (
                <CircularProgress size={16} />
              ) : undefined
            }
          >
            {createMutation.isPending
              ? 'Creating...'
              : isLoadingPreview
                ? 'Loading form...'
                : 'Create'}
          </ViewerGuardedButton>
        </DialogActions>
      </Dialog>

      {/* Document Fields Modal — shown after loss record is created */}
      <DocumentFieldsModal
        open={fieldsModalOpen}
        onClose={handleFieldsClose}
        onConfirm={handleFieldsConfirm}
        onSkip={handleFieldsSkip}
        previewData={previewData}
        isGenerating={isGeneratingPdf}
        title="DA 7923 — Statement of Charges"
        subtitle="Review and fill in any additional fields before generating the document. All fields are optional."
        confirmLabel="Generate Document"
        skipLabel="Skip Document"
        cancelLabel="Close"
      />
    </>
  );
};
